import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Tab, Tabs } from "@synapse-analytics/synapse-ui"

import { LogViewer } from "../../../../components/data-display/LogViewer"
import { FeatureImportanceChart } from "../../../../components/graphs/Bar/FeatureImportanceChart"
import { ChartsContainer } from "../../../../components/graphs/ChartsContainer"
import { ConfusionMatrixChart } from "../../../../components/graphs/HeatMap/ConfusionMatrixChart"
import { SankeyChart } from "../../../../components/graphs/SankeyChart"
import { EvaluationMetrics } from "../../../../components/tables/EvaluationMetrics/EvaluationMetrics"
import { getEnvVar, isEnvVariableTruthy } from "../../../../utils/genericHelpers"
import { NUM_OF_BARS_PER_PAGE } from "../../Constants"
import type { AutoMLV2Props } from "../../Interfaces"
import { CorrelationFunnel } from "./Graphs/CorrelationFunnel"
import { DistributionChart } from "./Graphs/DistributionChart"
import { LineChart } from "./Graphs/LineChart"
import { ThresholdSlidersField } from "./ThresholdSlidersField/ThresholdSlidersFields"

import styles from "./AutoMLV2.module.scss"

export function AutoMLV2(props: Readonly<AutoMLV2Props>): ReactElement {
  const {
    sliderValueChangeHandler,
    setCurrentDataTab,
    setCurrentImportanceChartPage,
    importanceChartSearchInputHandler,
    currentDataTab,
    disabled,
    confusionMatrixData,
    lineChartData,
    distributionChartData,
    sankeyChartData,
    correlationFunnelData,
    importanceChartData,
    currentImportanceChartPage,
    trainingLogs,
  } = props

  const isNPL_AREnabled = isEnvVariableTruthy(String(getEnvVar("AUTOML_NPL_AR_ENABLED")))
  const { t } = useTranslation("ModelConfigurations")

  return (
    <div>
      <ThresholdSlidersField onChangeHandler={sliderValueChangeHandler} disabled={disabled} />
      <section className={styles.metricsSectionContainer}>
        <header>
          <Tabs
            value={currentDataTab}
            onClick={(e) => setCurrentDataTab((e.target as HTMLInputElement).value as "data" | "business" | "training")}
            className={styles.tabs}
          >
            <Tab
              label={t("tabs.labels.dataScienceResults")}
              value={"data"}
              selected={currentDataTab === "data"}
              onClick={() => setCurrentDataTab("data")}
              size="large"
            />
            <Tab
              label={t("tabs.labels.businessResults")}
              value={"business"}
              selected={currentDataTab === "business"}
              onClick={() => setCurrentDataTab("business")}
              size="large"
            />
            <Tab
              label={t("tabs.labels.trainingMetrics")}
              value="training"
              selected={currentDataTab === "training"}
              onClick={() => setCurrentDataTab("training")}
              size="large"
            />
          </Tabs>
        </header>
        <div className={`${styles.dataContainer} scrollbar`}>
          {currentDataTab === "data" && (
            <>
              <EvaluationMetrics />
              <ConfusionMatrixChart chartData={confusionMatrixData} />
            </>
          )}
          {currentDataTab === "business" && (
            <>
              {isNPL_AREnabled && (
                <LineChart
                  lineChartData={lineChartData}
                  isLoading={false}
                  graphHeight={260}
                  title={t("charts.lineChart.titleNPLAR")}
                  tooltipText={t("charts.lineChart.tooltipNPLAR")}
                />
              )}
              <DistributionChart
                distributionChartData={distributionChartData}
                title={t("charts.distribution.title")}
                graphHeight={260}
                isLoading={false}
              />
              <SankeyChart
                title={t("charts.sankey.title")}
                graphHeight={250}
                data={sankeyChartData}
                chartType="config"
              />
            </>
          )}
          {currentDataTab === "training" && (
            <>
              <CorrelationFunnel
                chartData={correlationFunnelData}
                graphHeight={340}
                isLoading={false}
                title={t("charts.correlationFunnel.title")}
              />
              <FeatureImportanceChart
                chartData={importanceChartData}
                title={t("charts.featureImportance.title")}
                graphHeight={250}
                isLoading={false}
                numberOfBarsPerPage={NUM_OF_BARS_PER_PAGE}
                currentPage={currentImportanceChartPage}
                setCurrentPage={setCurrentImportanceChartPage}
                searchHandler={importanceChartSearchInputHandler}
              />
              <ChartsContainer title={t("charts.logs")} isLoading={false} gutterBottom graphHeight={"100%"}>
                <Grid item className={`${styles.logsContainer} "charts-paper-darker-mb-1"`}>
                  <LogViewer data={trainingLogs || []} customBorderRadius="4px" minHeight="30vh" />
                </Grid>
              </ChartsContainer>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
